import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  QUESTIONS: '/api/v4/phq-gad/questions/',
  RESPONSES: '/api/v4/phq-gad/responses/',
  METRIC_POINT: '/api/v3/metricpoint/',
} as const;

export type PhqGadChoice = {
  order: number;
  text: string;
  surveyChoiceUrn: string;
};

export type PhqGadQuestion = {
  key: string;
  order: number;
  title: string;
  text: string;
  additionalInfo: string;
  choices: PhqGadChoice[];
};

// Responses are submitted by the choice's index (its order on the 0–3 scale),
// not its urn — see Sparta's PhqGadResponse.
export type PhqGadResponse = {
  questionKey: string;
  choiceIndex: number;
};

export type PhqGadResult = {
  gadScore: number;
  phqScore: number;
  qualifiedProgramUrns: string[];
  requiresCrisisLine: boolean;
};

export type MetricPointPayload = {
  metric: string;
  user: string;
  value: number;
};

export async function fetchPhqGadQuestions(signal?: AbortSignal): Promise<PhqGadQuestion[]> {
  const res = await apiClient.get<{ items?: PhqGadQuestion[] }>(PATHS.QUESTIONS, { signal });
  return res.data?.items ?? [];
}

export async function submitPhqGadResponses(
  userUrn: string,
  responses: PhqGadResponse[]
): Promise<PhqGadResult | undefined> {
  const res = await apiClient.post<PhqGadResult>(
    PATHS.RESPONSES,
    decamelizeKeys({ userUrn, responses })
  );
  return res.data;
}

// Records a single metric point (e.g. phq-2 / gad-2 preliminary scores) so
// onboarding can resume / skip the survey later. Payload keys are already flat,
// so no decamelization. Mirrors Sparta's postMetricPoint.
export async function postMetricPoint(payload: MetricPointPayload): Promise<void> {
  await apiClient.post(PATHS.METRIC_POINT, payload);
}
